/**
 * BP-21 §9: the render loop's bookkeeping, separated from the render loop.
 *
 * The loop itself is `requestAnimationFrame`, a `<video>`, a WASM landmarker
 * and a WebGL context, none of which exist in a test and none of which are
 * where the mistakes are. What is here is what the loop *decides*: how fast
 * it is going, whether that is fast enough (rule 5), and which shades the
 * client has actually tried — the three things `ar_session` records and T7's
 * report is computed from.
 *
 * **The FPS figure is a rolling median, not a mean.** A mean over a window
 * is dragged down by one 400 ms stall — a notification, a garbage collection
 * — into saying a phone cannot do this when it can. The median says what the
 * client is actually seeing, which is the question rule 5 asks.
 *
 * **Shades are appended, never replaced.** The api does the same thing, for
 * the same reason: a client who tries a shade, moves on, and comes back has
 * tried it once, and a report that counted it twice would make the shade
 * look more popular than it is.
 */

/** Rule 5's floor, mirrored from `app.modules.ar.tryon.MIN_USABLE_FPS`. */
export const MIN_USABLE_FPS = 8;

/**
 * How many frames the median is taken over.
 *
 * About two seconds at a usable rate: long enough that one stalled frame
 * does not move it, short enough that the screen reacts before the client
 * has given up on it.
 */
export const FPS_WINDOW = 20;

/** The loop's state, as the screen reads it. */
export interface PipelineState {
  /** Frame intervals in milliseconds, newest last, at most `FPS_WINDOW`. */
  readonly intervals: readonly number[];
  /** SKU ids, in the order she first tried them. */
  readonly triedSkuIds: readonly string[];
  /** When the last frame arrived, in `performance.now()` milliseconds. */
  readonly lastFrameAt: number | null;
}

export const EMPTY: PipelineState = { intervals: [], triedSkuIds: [], lastFrameAt: null };

/**
 * Record a frame at `now`.
 *
 * The *first* frame contributes no interval — there is nothing to measure
 * from — which is why `lastFrameAt` is nullable rather than seeded with the
 * session's start. Seeding it would count the camera's warm-up as one very
 * slow frame and open every session in photo mode.
 */
export function onFrame(state: PipelineState, now: number): PipelineState {
  if (state.lastFrameAt === null) {
    return { ...state, lastFrameAt: now };
  }
  const interval = now - state.lastFrameAt;
  const intervals = [...state.intervals, interval].slice(-FPS_WINDOW);
  return { ...state, intervals, lastFrameAt: now };
}

/** The median frame interval, in milliseconds, or `null` before two frames. */
export function medianInterval(state: PipelineState): number | null {
  if (state.intervals.length === 0) {
    return null;
  }
  const sorted = [...state.intervals].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  // Selected rather than indexed. `noUncheckedIndexedAccess` makes
  // `sorted[middle]` a `number | undefined`, and the `?? 0` that silences it
  // is a branch no input can take — an uncoverable arm in a module whose
  // floor is 100 %. Filtering says the same thing with no unreachable code.
  const middling = sorted.filter(
    (_, index) => index === middle || (sorted.length % 2 === 0 && index === middle - 1),
  );
  return middling.reduce((sum, value) => sum + value, 0) / middling.length;
}

/** Frames per second, or `null` before there is anything to divide. */
export function fps(state: PipelineState): number | null {
  const interval = medianInterval(state);
  if (interval === null || interval <= 0) {
    return null;
  }
  return 1000 / interval;
}

/**
 * Rule 5: is this too slow to carry on with?
 *
 * `false` until the window is full. A session that opened photo mode because
 * its first three frames were slow would do it to every client on every
 * device — the first frames are always the slow ones, while the landmarker
 * warms up and the browser compiles the shaders.
 */
export function belowUsableFps(state: PipelineState): boolean {
  if (state.intervals.length < FPS_WINDOW) {
    return false;
  }
  const rate = fps(state);
  return rate !== null && rate < MIN_USABLE_FPS;
}

/** Note that she tried `skuId`. Idempotent, and keeps the order she tried them. */
export function tried(state: PipelineState, skuId: string): PipelineState {
  if (state.triedSkuIds.includes(skuId)) {
    return state;
  }
  return { ...state, triedSkuIds: [...state.triedSkuIds, skuId] };
}

/**
 * The FPS average as the api wants it: a two-place decimal string, or
 * `undefined` when nothing was measured.
 *
 * A string because `ar_session.fps_avg` is `NUMERIC(4,2)` and rule 5's
 * threshold is compared against it on the server; sending a float would put
 * a binary rounding error on the wrong side of 8.00 eventually.
 */
export function fpsForApi(state: PipelineState): string | undefined {
  const rate = fps(state);
  return rate === null ? undefined : rate.toFixed(2);
}
